import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import WorkflowSessionModal from "../../../WorkflowSessionModal";
import { api } from "../../../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { MoreHorizOutlined } from "@mui/icons-material";
import CustomValueModal from "../../../../Repository/ConnectionTypes/CustomValueModal";
import { debounce } from "lodash";
import TPCheckbox from "../../../../TemplateProperties/TPCheckbox";
import SmartPasswordField from "../../../../../commonComponents/SmartPasswordField";
import { decryptString, encryptString } from "../../../../../utils/utilFunctions";
import { compressionTypeOptions } from "../../../../../constants/constantList";

function BridgeGateFTP({ connectionDetails, handleBodyContent, disableControl, useConnection, repoPDC, fileMaskName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[state.treeData.currentScreen]);
  const currentTab = rightTree.currentTab;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const showSendDataControls = selectedNode?.ICON_DISPLAY === "SEND_DATA" || false;
  const { templateHeader } = useSelector((state) => state.treeData.workflow.rightTree.tree)
  const [currentValue, setCurrentValue] = useState("");
  const [modalType, setModalType] = useState("Account Value");
  const [openCustomModal, setOpenCustomModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [tradingPartners, setTradingPartners] = useState([]);
  const [tradingPartner, setTradingPartner] = useState(t("SELECT"));
  const [fileDirectory, setFileDirectory] = useState("");
  const [fileMask, setFileMask] = useState("");
  const [ssh, setSsh] = useState(false);
  const [deleteFile, setDeleteFile] = useState(false);
  const [decompression, setDecompression] = useState(false);
  const [compression, setCompression] = useState(false);
  const [compressionType, setCompressionType] = useState("ZIP");
  const [showDecompressionDir, setShowDecompressionDir] = useState(false);
  const [decompressionDir, setDecompressionDir] = useState('');
  const [fileNameWithinZip, setFileNameWithinZip] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [valueLabel, setValueLabel] = useState("");
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [disableCompression, setDisableCompression] = useState(false);
  const [workflowSessionModal, setWorkflowSessionModal] = useState({
    open: false,
    isFirstTime: false,
  });
  const workflowMappings = {
    [t("ACCOUNT_")]: {
      stateSetter: setAccount,
      key: "ACCOUNT",
      value: account,
    },
    [t("LBL_TRADE_PARTNER_USER_NM")]: {
      stateSetter: setTradingPartner,
      key: "TRADING_PARTNER_NAME",
      value: tradingPartner,
    },
    [t("LBL_FILE_DIR")]: {
      stateSetter: setFileDirectory,
      key: "FILE_LOCATION",
      value: fileDirectory,
    },
    [t("LBL_FILE_NAME_MASK")]: {
      stateSetter: setFileMask,
      key: "FILENAMEMASK",
      value: fileMask,
    },
    [t("LBL_FILE_NAME")]: {
      stateSetter: setFileMask,
      key: "FILENAME",
      value: fileMask,
    },
    [t('LBL_DECOMPRESS_DIRECTORY')]: {
      stateSetter: setDecompressionDir,
      key: "DECOMPRESSION_DIRECTORY",
      value: decompressionDir
    },
    [t('LBL_WS_PASSWORD')]: {
      stateSetter: setPassword,
      key: "ZIP_PASSWORD",
      value: password,
    },
    [t('FILE_NAME_WITHIN_ZIP')]: {
      stateSetter: setDecompressionDir,
      key: "FILE_NAME_WITHIN_ZIP",
      value: fileNameWithinZip
    },
  };
  const valuesHandler = (value) => {
    if (workflowMappings[valueLabel]) {
      const { stateSetter, key } = workflowMappings[valueLabel];
      stateSetter(value);
      onInputChange(key, value);
    }
  };

  const debouncedContentUpdate = debounce(handleBodyContent, 200);

  const onInputChange = (name, value) => {
    if (selectedNode?.INBOUND?.[name] === undefined || selectedNode.INBOUND[name] !== value) {
      if(name === 'ACCOUNT'){
         if(selectedNode?.INBOUND?.TRADING_PARTNER_NAME){
          debouncedContentUpdate({ add: {[name]:value, TRADING_PARTNER_NAME: null}});
         } else debouncedContentUpdate({ [name]: value });
      }
      else if(name === 'TRADING_PARTNER_NAME' && value === t('SELECT')){
         debouncedContentUpdate({remove: {TRADING_PARTNER_NAME: null}})
      }
      else debouncedContentUpdate({ [name]: value });
    }
  };
  
  function setCustomValue(value) {
    if (modalType === "Account Value") {
      onInputChange("ACCOUNT", value);
      setAccount(value);
    } else if (modalType === "Trading Partner Value") {
      onInputChange("TRADING_PARTNER_NAME", value);
      setTradingPartner(value);
    }
  }
  const getFontWeight = (control, currentValue, useConnection) => {
   if(control === 'TRADING_PARTNER_NAME' && currentValue === t('SELECT')) return 'normal';
    return repoPDC?.[control] !== currentValue &&
      useConnection === "preDefined"
      ? "bold"
      : "normal";
  };
  const fontWeights = useMemo(() => {
       return {
         FILE_LOCATION: getFontWeight(
           "FILE_LOCATION",
           fileDirectory,
           useConnection
         ),
         FILENAMEMASK: getFontWeight(
           "FILENAMEMASK",
           fileMask,
           useConnection
         ),
         DECOMPRESSION_DIRECTORY: getFontWeight(
           "DECOMPRESSION_DIRECTORY",
           decompressionDir,
           useConnection
         ),
         PASSWORD: getFontWeight(
           "ZIP_PASSWORD",
           password,
           useConnection
         ),
         ACCOUNT: getFontWeight(
           "ACCOUNT",
           account,
           useConnection
         ),
         TRADING_PARTNER_NAME: getFontWeight(
           "TRADING_PARTNER_NAME",
           tradingPartner,
           useConnection
         ),
       };
     }, [
       fileDirectory,
       fileMask,
       decompressionDir,
       password,
       account,
       tradingPartner,
       useConnection,
     ]);
    const handleDecompressionChange = (checked) => {
      setDecompression(checked);
  
      if (checked) {
        onInputChange("ZIP", "ZIP");
      } 
      else {
        setShowDecompressionDir(false);
        setDecompressionDir("");
        setShowPassword(false);
        setPassword("");
        onInputChange('add', { ZIP: null, SPECIFY_DECOMPRESSION_DIRECTORY: 'FALSE', DECOMPRESSION_DIRECTORY: null, USE_ZIP_PASSWORD: 'FALSE', ZIP_PASSWORD: null })
        
      }
    }
    
    const handleCompressionChange = (checked) => {
      setCompression(checked);
  
      if (checked) {
        onInputChange("ZIP", "ZIP");
      } 
      else {
        setShowDecompressionDir(false);
        setFileNameWithinZip("");
        setShowPassword(false);
        setPassword("");
        onInputChange('add', { ZIP: null, FILE_NAME_WITHIN_ZIP: null, DECOMPRESSION_DIRECTORY: null, USE_ZIP_PASSWORD: 'FALSE', ZIP_PASSWORD: null })
      }
    }

  const getTradingPatners = (acc) => {
    if(acc){
      dispatch(api.endpoints.getTradingPatners.initiate({ account: acc },{forceRefetch: true})).then(
        (response) => {
          if (response && response?.data?.current_state === 'SUCCESS') {
            if(response?.data?.data?.trading_partners.length > 0){

              const tradingPartners = [
                t('SELECT'),
                t('ALL'),
                ...(response?.data?.data?.trading_partners 
                  ? [...response.data.data.trading_partners].sort((a, b) => a.localeCompare(b)) 
                  : [])
              ];
              
              
              setTradingPartners(tradingPartners);
              setTradingPartner(connectionDetails?.TRADING_PARTNER_NAME || t('SELECT'))
            } else {
              setTradingPartners([t("EMPTY_COMBO")]);
              // setTradingPartner(t('EMPTY_COMBO'))
            }
            
          } else setTradingPartners([]);
        }
      );
    } else {
      setTradingPartners([t('SELECT'), t('ALL')]);
      setTradingPartner(connectionDetails?.TRADING_PARTNER_NAME || t('SELECT'))
    }
    
  };

  useEffect(() => {
    const getValue = (key, fallback) => {
        return connectionDetails?.[key] || fallback;
      };
    
    setAccount(getValue('ACCOUNT', ''));
    setTradingPartner(getValue('TRADING_PARTNER_NAME', t('SELECT')));
    setFileDirectory(getValue('FILE_LOCATION', ''));
    setFileMask(getValue(showSendDataControls ? 'FILENAME' : 'FILENAMEMASK', ''));
    setSsh(getValue('SSH', false) === 'TRUE' || getValue('SSH', false) === true);
    setDeleteFile(getValue('DELETE', true) === 'FALSE' || getValue('DELETE', true) === false);
    setDecompression(getValue('ZIP', false) === 'ZIP');
    setCompression(["GZ", "JAR", "RAR", "TAR", "TGZ", "ZIP"].includes(getValue('ZIP', "")));
    setCompressionType(getValue('ZIP', 'ZIP') || 'ZIP');
    setFileNameWithinZip(getValue('FILE_NAME_WITHIN_ZIP', ''));
    setShowDecompressionDir((getValue("DECOMPRESSION_DIRECTORY",'') && getValue("DECOMPRESSION_DIRECTORY",'').length !== 0 )|| getValue('SPECIFY_DECOMPRESSION_DIRECTORY',false));
    setDecompressionDir(getValue('DECOMPRESSION_DIRECTORY', ''))
    setShowPassword(getValue('USE_ZIP_PASSWORD',false) === 'TRUE' || getValue('USE_ZIP_PASSWORD',false) === true);
    // setPassword(getValue('ZIP_PASSWORD',''));
    setPassword(connectionDetails?.ZIP_PASSWORD ? decryptString(getValue('ZIP_PASSWORD',''), process.env.REACT_APP_SECRET_KEY2) : '') ;

    dispatch(api.endpoints.getAccounts.initiate()).then((response) => {
      setAccounts(response?.data?.current_state === "SUCCESS" ? response?.data?.data?.accounts : []);
    });
  
    // getTradingPatners(params.account);
 
  }, [connectionDetails, connectionDetails?.OVERRIDE]);
  
  useEffect(() => {
    getTradingPatners(account);
  }, [account]);

   useEffect(() => {
    if (templateHeader && templateHeader?.hasOwnProperty("SERVICE") && templateHeader?.SERVICE.NAME === selectedNode?.title && templateHeader?.SERVICE?.ENABLED === "TRUE") {
      setShowCheckbox(true);
    } else {
      setShowCheckbox(false);
    }
  }, [templateHeader]);

  useEffect(() => {
    if(selectedNode?.INBOUND?.DONT_RETAIN_PLAIN_TEXT === "TRUE" || (selectedNode?.INBOUND?.hasOwnProperty("ENCRYPTION_TYPE") && selectedNode?.INBOUND?.ENCRYPTION_TYPE !== "NONE")
    || selectedNode?.OUTBOUND?.DONT_RETAIN_PLAIN_TEXT === "TRUE" || (selectedNode?.OUTBOUND?.hasOwnProperty("ENCRYPTION_TYPE") && selectedNode?.OUTBOUND?.ENCRYPTION_TYPE !== "NONE")){
        setDisableCompression(true);
    } else {
        setDisableCompression(false);
    }
}, [selectedNode?.INBOUND, selectedNode?.OUTBOUND]);

  return (
    <>
      {/* Accounts Dropdown */}
      <div className="flex" style={{ flex: "1", width: "100%" }}>
        <Autocomplete
          disablePortal
          options={accounts}
          value={account}
          disabled={disableControl || false}
          freeSolo
          sx={{
            maxHeight: 300,
            width: "100%",
            marginRight: "4px",
            input: { fontWeight: fontWeights.ACCOUNT },
          }}
          onChange={(e, value) => {
            onInputChange("ACCOUNT", value ?? "");
            setAccount(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("ACCOUNT_")}
              value={account}
              onChange={(e) => {
                onInputChange("ACCOUNT", e.target.value ?? "");
                setAccount(e.target.value);
              }}
              variant="outlined"
              size="small"
              name={"ACCOUNT"}
              autoComplete="off"
            />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={disableControl || false}
          onClick={() => {
            setModalType("Account Value");
            setOpenCustomModal(true);
            setCurrentValue(account);
          }}
          className="muiButtonHeight"
          style={styles.popupBtn}>
          {t("BTN_ABC")}
        </Button>
        <Button
          size="small"
          disabled={disableControl || false}
          variant="outlined"
          onClick={() => {
            setValueLabel(t("ACCOUNT_"));
            setWorkflowSessionModal({ open: true, isFirstTime: true });
          }}
          sx={{ width: "40px", minWidth: "40px", marginLeft: "4px" }}>
          <MoreHorizOutlined fontSize="small" />
        </Button>
      </div>
      {/* Trading Partner Dropdown */}
      <div className="flex" style={{ flex: "1", width: "100%" }}>
        <Autocomplete
          disablePortal
          disabled={disableControl || false}
          options={tradingPartners}
          value={tradingPartner}
          freeSolo
          sx={{
            maxHeight: 300,
            width: "100%",
            marginRight: "4px",
            input: { fontWeight: fontWeights.TRADING_PARTNER_NAME },
          }}
          onChange={(e, value) => {
            // if (value !== "-- Select --")
            onInputChange("TRADING_PARTNER_NAME", value);
            setTradingPartner(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("LBL_TRADE_PARTNER_USER_NM")}
              value={tradingPartner || tradingPartners[0]}
              onChange={(event) => {
                let value = event.target.value;
                if (value !== "-- Select --")
                  onInputChange("TRADING_PARTNER_NAME", value);
                setTradingPartner(value);
              }}
              variant="outlined"
              size="small"
              name={"TRADING_PARTNER"}
              autoComplete="off"
            />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={disableControl || false}
          onClick={() => {
            setModalType("Trading Partner Value");
            setOpenCustomModal(true);
            setCurrentValue(tradingPartner);
          }}
          className="muiButtonHeight"
          style={styles.popupBtn}>
          {t("BTN_ABC")}
        </Button>
        <Button
          size="small"
          disabled={disableControl || false}
          variant="outlined"
          onClick={() => {
            setValueLabel(t("LBL_TRADE_PARTNER_USER_NM"));
            setWorkflowSessionModal({ open: true, isFirstTime: true });
          }}
          sx={{ width: "40px", minWidth: "40px", marginLeft: "4px" }}>
          <MoreHorizOutlined fontSize="small" />
        </Button>
      </div>
      {/* File Directory */}
      <div className="flex">
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            disabled={disableControl || false}
            label={t("LBL_FILE_DIR")}
            type="text"
            value={fileDirectory}
            sx={{
              input: {
                fontWeight: fontWeights.FILE_LOCATION,
              },
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setFileDirectory(e.target.value);
            }}
            className={"width-100"}
            name={"FILE_LOCATION"}
            autoComplete="off"
          />
        </FormControl>
        <Button
          size="small"
          disabled={disableControl || false}
          variant="outlined"
          onClick={() => {
            setValueLabel(t("LBL_FILE_DIR"));
            setWorkflowSessionModal({ open: true, isFirstTime: true });
          }}
          sx={{ width: "40px", minWidth: "40px", marginLeft: "4px" }}>
          <MoreHorizOutlined fontSize="small" />
        </Button>
      </div>
      {/* File Name Mask */}
      <div className="flex">
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            disabled={fileMaskName?.includes("*") || fileMaskName?.includes("?") || disableControl || false}
            label={showSendDataControls ? t("LBL_FILE_NAME") : t("LBL_FILE_NAME_MASK")}
            type="text"
            value={fileMask}
            sx={{
              input: {
                fontWeight: fontWeights.FILENAMEMASK,
              },
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setFileMask(e.target.value);
            }}
            className={"width-100"}
            name={showSendDataControls ? "FILENAME" : "FILENAMEMASK"}
            autoComplete="off"
          />
        </FormControl>
        <Button
          size="small"
          disabled={fileMaskName?.includes("*") || fileMaskName?.includes("?") || disableControl || false}
          variant="outlined"
          onClick={() => {
            setValueLabel(showSendDataControls ? t("LBL_FILE_NAME") : t("LBL_FILE_NAME_MASK"));
            setWorkflowSessionModal({ open: true, isFirstTime: true });
          }}
          sx={{ width: "40px", minWidth: "40px", marginLeft: "4px" }}>
          <MoreHorizOutlined fontSize="small" />
        </Button>
      </div>
      {/* Checkboxes */}
      <div className="flex flex-wrap">
        {showSendDataControls ?
          <TPCheckbox
            label={t("BTN_USE_ZIP_COMPRESS")}
            checked={compression}
            disabled={disableControl || disableCompression || false}
            id="bridgeGateFTPCompression"
            handleCheck={(checked) => handleCompressionChange(checked)}
          />
          :
          <>
            <TPCheckbox
              label={t("BTN_SSH")}
              checked={ssh}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange("SSH", checked ? "TRUE" : "FALSE");
                setSsh(checked);
              }}
            />
            {!showCheckbox && (
              <TPCheckbox
                label={t("BTN_DO_NOT_DELETE")}
                checked={deleteFile}
                disabled={disableControl || false}
                handleCheck={(checked) => {
                  onInputChange("DELETE", (!checked ? 'TRUE' : 'FALSE'));
                  setDeleteFile(checked);
                }}
              />
            )}
            <TPCheckbox
              label={t("BTN_USE_ZIP_DECOMPRESS")}
              checked={decompression}
              disabled={disableControl || disableCompression || false}
              id="bridgeGateFTPDecompression"
              handleCheck={(checked) => {
                handleDecompressionChange(checked);
              }}
            />
          </>}
      </div>

      {compression && showSendDataControls &&
        <>
          <FormControl
            variant="outlined"
            className="width-100"
            size="small"
          >
            <InputLabel
            >
              {t("LBL_COMPRESSION_TYPE")}
            </InputLabel>
            <Select
              disabled={disableControl || false}
              variant="outlined"
              size="small"
              className={"width-100"}
              value={compressionType}
              onChange={(e) => {
                setCompressionType(e.target.value);
                onInputChange("ZIP", e.target.value);
              }}
            >
              {compressionTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>))}
            </Select>
          </FormControl>

          <div style={{ display: "flex", gap: "4px" }}>
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                disabled={disableControl || false}
                label={t("FILE_NAME_WITHIN_ZIP")}
                type="text"
                value={fileNameWithinZip || ""}
                sx={{
                  input: {
                    fontWeight: fontWeights.DECOMPRESSION_DIRECTORY,
                  },
                }}
                onChange={(e) => {
                  onInputChange(e.target.name, e.target.value);
                  setFileNameWithinZip(e.target.value);
                }}
                className={"width-100"}
                name={"FILE_NAME_WITHIN_ZIP"}
                autoComplete="off"
              />
            </FormControl>
            <Button
              size="small"
              disabled={disableControl || false}
              variant="outlined"
              onClick={() => {
                setValueLabel(t("FILE_NAME_WITHIN_ZIP"));
                setWorkflowSessionModal({
                  open: true,
                  isFirstTime: true,
                });
              }}
              sx={{ width: "40px", minWidth: "40px" }}>
              <MoreHorizOutlined fontSize="small" />
            </Button>
          </div>

        {["JAR", "RAR", "ZIP"].includes(compressionType) &&
          <>
            <div>
              <TPCheckbox
                label={t("LBL_WS_PASSWORD")}
                checked={showPassword}
                disabled={disableControl || false}
                handleCheck={(checked) => {
                  onInputChange(
                    "USE_ZIP_PASSWORD", (checked));
                  setShowPassword(checked);
                  if (!checked) {
                    setPassword("");
                    onInputChange("ZIP_PASSWORD", "");
                  }
                }}
              />
            </div>

            {showPassword && (
              <div style={{ display: 'flex', gap: "4px" }}>
                <SmartPasswordField
                  variant="outlined"
                  size="small"
                  disabled={disableControl || false}
                  label={t("LBL_WS_PASSWORD")}
                  type="password"
                  value={password || ""}
                  sx={{
                    input: {
                      fontWeight: fontWeights.PASSWORD,
                    },
                  }}
                  onChange={(e) => {
                    onInputChange(e.target.name, encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2));
                    setPassword(e.target.value);
                  }}
                  className={"width-100"}
                  name={"ZIP_PASSWORD"}
                  autoComplete="off"
                />
                <Button
                  size="small"
                  disabled={disableControl || false}
                  variant="outlined"
                  onClick={() => {
                    setValueLabel(t("LBL_WS_PASSWORD"));
                    setWorkflowSessionModal({
                      open: true,
                      isFirstTime: true,
                    });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              </div>
            )}
          </>}
        </>
      }

      {!showSendDataControls && decompression && (
        <>
          <div>
            <TPCheckbox
              label={t("LBL_SPECIFY_DECOMPRESSION_DIRECTORY")}
              checked={showDecompressionDir}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange(
                  "SPECIFY_DECOMPRESSION_DIRECTORY",
                  (checked ? 'TRUE' : 'FALSE')
                );
                setShowDecompressionDir(checked);
                if (!checked) {
                  onInputChange("DECOMPRESSION_DIRECTORY", "");
                  setDecompressionDir("");
                }
              }}
            />
          </div>

          {showDecompressionDir && (
            <div style={{ display: "flex", gap: "4px" }}>
              <FormControl className="width-100" size="small">
                <TextField
                  variant="outlined"
                  size="small"
                  disabled={disableControl || false}
                  label={t("LBL_DECOMPRESS_DIRECTORY")}
                  type="text"
                  value={decompressionDir || ""}
                  sx={{
                    input: {
                      fontWeight: fontWeights.DECOMPRESSION_DIRECTORY,
                    },
                  }}
                  onChange={(e) => {
                    onInputChange(e.target.name, e.target.value);
                    setDecompressionDir(e.target.value);
                  }}
                  className={"width-100"}
                  name={"DECOMPRESSION_DIRECTORY"}
                  autoComplete="off"
                />
              </FormControl>
                <Button
                  size="small"
                  disabled={disableControl || false}
                  variant="outlined"
                  onClick={() => {
                    setValueLabel(t("LBL_DECOMPRESS_DIRECTORY"));
                    setWorkflowSessionModal({
                      open: true,
                      isFirstTime: true,
                    });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
            </div>
          )}
          <div>
            <TPCheckbox
              label={t("LBL_WS_PASSWORD")}
              checked={showPassword}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange(
                  "USE_ZIP_PASSWORD",(checked));
                setShowPassword(checked);
                if (!checked) {
                  setPassword("");
                  onInputChange("ZIP_PASSWORD", "");
                }
              }}
            />
          </div>
          {showPassword && (
            <div style={{ display: 'flex', gap: "4px" }}>
              <SmartPasswordField
                variant="outlined"
                size="small"
                disabled={disableControl || false}
                label={t("LBL_WS_PASSWORD")}
                type="password"
                value={password || ""}
                sx={{
                  input: {
                    fontWeight: fontWeights.PASSWORD,
                  },
                }}
                onChange={(e) => {
                  onInputChange(e.target.name, encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2));
                  setPassword(e.target.value);
                }}
                className={"width-100"}
                name={"ZIP_PASSWORD"}
                autoComplete="off"
              />
              <Button
                size="small"
                disabled={disableControl || false}
                variant="outlined"
                onClick={() => {
                  setValueLabel(t("LBL_WS_PASSWORD"));
                  setWorkflowSessionModal({
                    open: true,
                    isFirstTime: true,
                  });
                }}
                sx={{ width: "40px", minWidth: "40px" }}>
                <MoreHorizOutlined fontSize="small" />
              </Button>
            </div>
          )}
        </>
      )}
      <CustomValueModal
        type={modalType}
        modalOpen={openCustomModal}
        currentValue={currentValue}
        setValue={setCustomValue}
        modalType={modalType}
        handleClose={() => setOpenCustomModal(false)}
      />
      <WorkflowSessionModal
        valueLabel={valueLabel}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={() =>
          setWorkflowSessionModal({ open: false, isFirstTime: false })
        }
        initialValue={workflowMappings[valueLabel]?.value}
        valuesHandler={(value) => {
          valuesHandler(value);
        }}
        inputType={valueLabel === t("LBL_WS_PASSWORD") ? 'password' : 'text'}
        specialPasswordField={valueLabel === t("LBL_WS_PASSWORD")}
      />
    </>
  );
}

export default BridgeGateFTP;
const styles = {
  popupBtn: {
    minWidth: "32px",
  },
};
